import { Head, Link, useForm } from '@inertiajs/react'
import { useState } from 'react'
import AdminLayout from '../../components/layout/AdminLayout'
import Input from '../../components/ui/Input'
import Select from '../../components/ui/Select'
import Textarea from '../../components/ui/Textarea'

export default function LeadsShow({ lead, conversations, qr_code }) {
  const [isEditing, setIsEditing] = useState(false)
  const { data, setData, patch, processing, errors, reset } = useForm({
    lead: {
      first_name: lead.first_name || '',
      last_name: lead.last_name || '',
      email: lead.email || '',
      phone: lead.phone || '',
      project_type: lead.project_type || '',
      property_type: lead.property_type || '',
      budget: lead.budget || '',
      location: lead.location || '',
      timeline: lead.timeline || '',
      status: lead.status || 'new',
      notes: lead.notes || ''
    }
  })

  const handleSubmit = (e) => {
    e.preventDefault()
    patch(`/leads/${lead.id}`, {
      onSuccess: () => setIsEditing(false)
    })
  }

  const handleCancel = () => {
    reset()
    setIsEditing(false)
  }

  const setField = (field, value) => {
    setData('lead', { ...data.lead, [field]: value })
  }

  return (
    <AdminLayout>
      <Head title={`Lead - ${lead.full_name}`} />

      <div>
        <div className="mx-auto max-w-7xl">
          {/* Header with Back Button */}
          <div className="mb-8">
            <Link
              href="/leads"
              className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-700 mb-4"
            >
              &larr; Retour aux leads
            </Link>
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">{lead.full_name}</h1>
                <p className="mt-2 text-sm text-gray-600">
                  Cr&eacute;&eacute; le {new Date(lead.created_at).toLocaleDateString('fr-FR', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <StatusBadge status={lead.status} large />
                {!isEditing && (
                  <button
                    onClick={() => setIsEditing(true)}
                    className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Modifier
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            {/* Left Column - Lead Details */}
            <div className="lg:col-span-2 space-y-6">
              {isEditing ? (
                /* Edit Form */
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Contact Information - Edit Mode */}
                  <div className="bg-white rounded-lg shadow p-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Informations de contact</h2>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <Input
                        label="Pr&eacute;nom"
                        name="first_name"
                        value={data.lead.first_name}
                        onChange={(e) => setField('first_name', e.target.value)}
                        error={errors['lead.first_name']}
                        required
                      />
                      <Input
                        label="Nom"
                        name="last_name"
                        value={data.lead.last_name}
                        onChange={(e) => setField('last_name', e.target.value)}
                        error={errors['lead.last_name']}
                        required
                      />
                      <Input
                        label="Email"
                        type="email"
                        name="email"
                        value={data.lead.email}
                        onChange={(e) => setField('email', e.target.value)}
                        error={errors['lead.email']}
                      />
                      <Input
                        label="T&eacute;l&eacute;phone"
                        type="tel"
                        name="phone"
                        value={data.lead.phone}
                        onChange={(e) => setField('phone', e.target.value)}
                        error={errors['lead.phone']}
                      />
                    </div>
                  </div>

                  {/* Project Information - Edit Mode */}
                  <div className="bg-white rounded-lg shadow p-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Projet</h2>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <Select
                        label="Type de projet"
                        name="project_type"
                        value={data.lead.project_type}
                        onChange={(e) => setField('project_type', e.target.value)}
                        error={errors['lead.project_type']}
                        options={[
                          { value: 'buyer', label: 'Achat' },
                          { value: 'seller', label: 'Vente' },
                          { value: 'tenant', label: 'Location' },
                          { value: 'landlord', label: 'Propri&eacute;taire' }
                        ]}
                      />
                      <Input
                        label="Type de bien"
                        name="property_type"
                        value={data.lead.property_type}
                        onChange={(e) => setField('property_type', e.target.value)}
                        error={errors['lead.property_type']}
                        placeholder="Ex: Appartement, Maison..."
                      />
                      <Input
                        label="Budget (&euro;)"
                        type="number"
                        name="budget"
                        value={data.lead.budget}
                        onChange={(e) => setField('budget', e.target.value)}
                        error={errors['lead.budget']}
                      />
                      <Input
                        label="Localisation"
                        name="location"
                        value={data.lead.location}
                        onChange={(e) => setField('location', e.target.value)}
                        error={errors['lead.location']}
                        placeholder="Ex: Paris 15&egrave;me"
                      />
                      <Input
                        label="&Eacute;ch&eacute;ance"
                        name="timeline"
                        value={data.lead.timeline}
                        onChange={(e) => setField('timeline', e.target.value)}
                        error={errors['lead.timeline']}
                        placeholder="Ex: Dans 3 mois"
                      />
                      <Select
                        label="Statut"
                        name="status"
                        value={data.lead.status}
                        onChange={(e) => setField('status', e.target.value)}
                        error={errors['lead.status']}
                        options={[
                          { value: 'new', label: 'Nouveau' },
                          { value: 'contacted', label: 'Contact&eacute;' },
                          { value: 'qualified', label: 'Qualifi&eacute;' },
                          { value: 'converted', label: 'Converti' },
                          { value: 'lost', label: 'Perdu' }
                        ]}
                      />
                    </div>
                  </div>

                  {/* Notes - Edit Mode */}
                  <div className="bg-white rounded-lg shadow p-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Notes</h2>
                    <Textarea
                      name="notes"
                      value={data.lead.notes}
                      onChange={(e) => setField('notes', e.target.value)}
                      error={errors['lead.notes']}
                      rows={4}
                      placeholder="Notes internes sur ce lead..."
                    />
                  </div>

                  {/* Form Actions */}
                  <div className="flex gap-4 pt-4">
                    <button
                      type="submit"
                      disabled={processing}
                      className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                    >
                      {processing ? 'Enregistrement...' : 'Enregistrer les modifications'}
                    </button>
                    <button
                      type="button"
                      onClick={handleCancel}
                      disabled={processing}
                      className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 transition-colors"
                    >
                      Annuler
                    </button>
                  </div>
                </form>
              ) : (
                /* Read-Only View */
                <>
                  {/* Contact Information - Read Mode */}
                  <div className="bg-white rounded-lg shadow p-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Informations de contact</h2>
                    <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Pr&eacute;nom</dt>
                        <dd className="mt-1 text-sm text-gray-900">{lead.first_name}</dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Nom</dt>
                        <dd className="mt-1 text-sm text-gray-900">{lead.last_name}</dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Email</dt>
                        <dd className="mt-1 text-sm text-gray-900">
                          {lead.email ? (
                            <a href={`mailto:${lead.email}`} className="text-blue-600 hover:text-blue-800">
                              {lead.email}
                            </a>
                          ) : (
                            <span className="text-gray-400">Non renseign&eacute;</span>
                          )}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">T&eacute;l&eacute;phone</dt>
                        <dd className="mt-1 text-sm text-gray-900">
                          {lead.phone ? (
                            <a href={`tel:${lead.phone}`} className="text-blue-600 hover:text-blue-800">
                              {lead.phone}
                            </a>
                          ) : (
                            <span className="text-gray-400">Non renseign&eacute;</span>
                          )}
                        </dd>
                      </div>
                    </dl>
                  </div>

                  {/* Project Information - Read Mode */}
                  <div className="bg-white rounded-lg shadow p-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Projet</h2>
                    <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Type de projet</dt>
                        <dd className="mt-1">
                          <span className="inline-flex px-2 text-xs font-semibold leading-5 text-gray-800 bg-gray-100 rounded-full">
                            {lead.project_type}
                          </span>
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Type de bien</dt>
                        <dd className="mt-1 text-sm text-gray-900">
                          {lead.property_type || <span className="text-gray-400">Non renseign&eacute;</span>}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Budget</dt>
                        <dd className="mt-1 text-sm text-gray-900">{lead.budget_formatted}</dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Localisation</dt>
                        <dd className="mt-1 text-sm text-gray-900">
                          {lead.location || <span className="text-gray-400">Non renseign&eacute;</span>}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">&Eacute;ch&eacute;ance</dt>
                        <dd className="mt-1 text-sm text-gray-900">
                          {lead.timeline || <span className="text-gray-400">Non renseign&eacute;</span>}
                        </dd>
                      </div>
                    </dl>
                  </div>
                </>
              )}

              {/* Conversations History */}
              {conversations.length > 0 ? (
                <div className="bg-white rounded-lg shadow p-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center justify-between">
                    <span>Historique de la conversation IA</span>
                    <span className="text-sm font-normal text-gray-500">
                      {conversations.length} message{conversations.length > 1 ? 's' : ''}
                    </span>
                  </h2>

                  <div className="bg-gray-50 rounded-lg p-4 mb-4">
                    <p className="text-sm text-gray-600">
                      <strong>Conversation automatique</strong> : Notre assistant IA a qualifi&eacute; ce lead en collectant
                      les informations essentielles (nom, contact, projet). Vous pouvez voir ci-dessous l'historique
                      complet de l'&eacute;change.
                    </p>
                  </div>

                  <div className="space-y-3 max-h-[600px] overflow-y-auto pr-2">
                    {conversations.map((conv, index) => (
                      <div
                        key={conv.id}
                        className={`p-4 rounded-lg border-2 ${
                          conv.role === 'user'
                            ? 'bg-blue-50 border-blue-200 ml-8'
                            : 'bg-white border-gray-200 mr-8'
                        }`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className={`text-xs font-semibold px-2 py-1 rounded ${
                              conv.role === 'user'
                                ? 'bg-blue-600 text-white'
                                : 'bg-purple-600 text-white'
                            }`}>
                              {conv.role === 'user' ? 'Visiteur' : 'Assistant IA'}
                            </span>
                            {index === 0 && (
                              <span className="text-xs text-gray-500">(D&eacute;but de conversation)</span>
                            )}
                          </div>
                          <span className="text-xs text-gray-500">
                            {new Date(conv.created_at).toLocaleTimeString('fr-FR', {
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </span>
                        </div>

                        <p className="text-sm text-gray-900 leading-relaxed whitespace-pre-wrap">
                          {conv.content}
                        </p>

                        {conv.role === 'assistant' && conv.model && (
                          <div className="mt-3 pt-2 border-t border-gray-200 flex items-center gap-4 text-xs text-gray-500">
                            <span>Mod&egrave;le: {conv.model}</span>
                            {conv.tokens_used && <span>Tokens: {conv.tokens_used}</span>}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-lg shadow p-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">
                    Historique de conversation
                  </h2>
                  <div className="bg-gray-50 rounded-lg p-6 text-center">
                    <p className="text-gray-500">
                      Aucune conversation enregistr&eacute;e pour ce lead.
                    </p>
                  </div>
                </div>
              )}

              {/* Notes */}
              {lead.notes && (
                <div className="bg-white rounded-lg shadow p-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">Notes</h2>
                  <p className="text-sm text-gray-900 whitespace-pre-wrap">{lead.notes}</p>
                </div>
              )}
            </div>

            {/* Right Column - Meta Information */}
            <div className="space-y-6">
              {/* Status & Assignment */}
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-sm font-semibold text-gray-900 mb-4">Gestion</h3>
                <dl className="space-y-3">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Statut</dt>
                    <dd className="mt-1">
                      <StatusBadge status={lead.status} />
                    </dd>
                  </div>
                  {lead.assigned_to && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Assign&eacute; &agrave;</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {lead.assigned_to.first_name} {lead.assigned_to.last_name}
                      </dd>
                    </div>
                  )}
                  {lead.qualified_at && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Qualifi&eacute; le</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {new Date(lead.qualified_at).toLocaleDateString('fr-FR')}
                      </dd>
                    </div>
                  )}
                </dl>
              </div>

              {/* Source Information */}
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-sm font-semibold text-gray-900 mb-4">Source</h3>
                <dl className="space-y-3">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Source</dt>
                    <dd className="mt-1 text-sm text-gray-900">{lead.source || 'qr_code'}</dd>
                  </div>
                  {qr_code && (
                    <>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">QR Code</dt>
                        <dd className="mt-1 text-sm text-gray-900">{qr_code.code}</dd>
                      </div>
                      {qr_code.property_address && (
                        <div>
                          <dt className="text-sm font-medium text-gray-500">Bien concern&eacute;</dt>
                          <dd className="mt-1 text-sm text-gray-900">{qr_code.property_address}</dd>
                        </div>
                      )}
                    </>
                  )}
                  {lead.utm_campaign && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Campagne</dt>
                      <dd className="mt-1 text-sm text-gray-900">{lead.utm_campaign}</dd>
                    </div>
                  )}
                  {lead.utm_source && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Source UTM</dt>
                      <dd className="mt-1 text-sm text-gray-900">{lead.utm_source}</dd>
                    </div>
                  )}
                </dl>
              </div>

              {/* Metadata */}
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-sm font-semibold text-gray-900 mb-4">M&eacute;tadonn&eacute;es</h3>
                <dl className="space-y-3">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Cr&eacute;&eacute; le</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {new Date(lead.created_at).toLocaleString('fr-FR')}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Mis &agrave; jour le</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {new Date(lead.updated_at).toLocaleString('fr-FR')}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Nombre de conversations</dt>
                    <dd className="mt-1 text-sm text-gray-900">{conversations.length}</dd>
                  </div>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  )
}

// StatusBadge Component
function StatusBadge({ status, large = false }) {
  const statusConfig = {
    new: { label: 'Nouveau', color: 'bg-gray-100 text-gray-800' },
    contacted: { label: 'Contact\u00e9', color: 'bg-yellow-100 text-yellow-800' },
    qualified: { label: 'Qualifi\u00e9', color: 'bg-blue-100 text-blue-800' },
    converted: { label: 'Converti', color: 'bg-green-100 text-green-800' },
    lost: { label: 'Perdu', color: 'bg-red-100 text-red-800' },
  }

  const config = statusConfig[status] || statusConfig.new
  const sizeClass = large ? 'px-4 py-2 text-sm' : 'px-2 text-xs'

  return (
    <span className={`inline-flex font-semibold leading-5 rounded-full ${config.color} ${sizeClass}`}>
      {config.label}
    </span>
  )
}
